import React from 'react';
import { CalendarClock, Layers, Radio, TrendingUp } from 'lucide-react';
import { formatWeekdayShort } from '../../utils/ipoCalendar';

const Card = ({ label, icon: Icon, accent, children }) => (
  <li className="relative min-w-0 overflow-hidden bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
    <div className="flex items-start justify-between gap-2">
      <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <span className="w-7 h-7 rounded-lg border border-slate-200 bg-slate-50 text-slate-400 flex items-center justify-center" aria-hidden="true">
        <Icon className="w-3.5 h-3.5" />
      </span>
    </div>
    {children}
    <span className={`absolute inset-x-0 bottom-0 h-1 ${accent}`} aria-hidden="true" />
  </li>
);

const shortName = (company) => company.replace(/\s+(Ltd\.?|Limited)$/i, '');

export const SmeStatCards = ({ stats }) => {
  const { total, nse, bse, open, closingToday, upcomingCount, nextOpening, topGmp } = stats;
  const soonestClose = [...open].sort((a, b) => (a.closeOn || '').localeCompare(b.closeOn || ''))[0];

  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      <Card label="Total SME IPOs Tracked" icon={Layers} accent="bg-[#008080]">
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <p className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{total} Issues</p>
          <span className="shrink-0 px-2 py-0.5 rounded-md border border-teal-100 bg-teal-50 text-[11px] font-extrabold text-[#006666] whitespace-nowrap">
            {nse} NSE / {bse} BSE
          </span>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Across NSE Emerge &amp; BSE SME platforms</p>
      </Card>

      <Card label="Open for Bidding" icon={Radio} accent="bg-emerald-500">
        <p className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-emerald-700">
          {open.length} {open.length === 1 ? 'Issue' : 'Issues'}
        </p>
        <p className="mt-2 text-[11px] font-semibold text-emerald-700 truncate">
          {closingToday.length
            ? `${shortName(closingToday[0].company)} closes at 5 PM today${closingToday.length > 1 ? ` · +${closingToday.length - 1} more` : ''}`
            : soonestClose
              ? `${shortName(soonestClose.company)} closes ${soonestClose.closeOn ? formatWeekdayShort(soonestClose.closeOn) : 'soon'}`
              : 'No SME IPO is open right now'}
        </p>
      </Card>

      <Card label="Next SME Opening" icon={CalendarClock} accent="bg-sky-500">
        {nextOpening ? (
          <>
            <p className="mt-2 text-lg sm:text-xl font-extrabold tracking-tight text-slate-900 truncate" title={nextOpening.company}>
              {shortName(nextOpening.company)}
            </p>
            <p className="mt-1 text-[11px] font-semibold text-sky-700 truncate">
              {nextOpening.openOn ? `Opens ${formatWeekdayShort(nextOpening.openOn)}` : 'Dates to be announced'}
              {nextOpening.priceBand ? ` · ${nextOpening.priceBand}` : ''}
              {upcomingCount > 1 ? ` · ${upcomingCount - 1} more upcoming` : ''}
            </p>
          </>
        ) : (
          <>
            <p className="mt-2 text-lg sm:text-xl font-extrabold tracking-tight text-slate-400">None scheduled</p>
            <p className="mt-1 text-[11px] text-slate-400">New dates appear once NSE announces them</p>
          </>
        )}
      </Card>

      <Card label="Highest SME GMP" icon={TrendingUp} accent="bg-amber-400">
        {topGmp ? (
          <>
            <p className={`mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight ${topGmp.gmpPercent >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {topGmp.gmpPercent >= 0 ? '+' : ''}
              {topGmp.gmpPercent.toFixed(1)}%
            </p>
            <p className="mt-2 text-[11px] font-semibold text-slate-600 truncate">
              {shortName(topGmp.company)} · GMP {topGmp.gmp >= 0 ? '+' : '−'}₹{Math.abs(topGmp.gmp)}
            </p>
          </>
        ) : (
          <>
            <p className="mt-2 text-lg sm:text-xl font-extrabold tracking-tight text-slate-400">Awaiting GMP</p>
            <p className="mt-1 text-[11px] text-slate-400">Grey market rates usually appear close to opening</p>
          </>
        )}
      </Card>
    </ul>
  );
};

export default SmeStatCards;
